// Single source of truth for "which languages exist" (D7).
//
// Adding a language is one row here plus a src/locales/<code>/ folder.
// Nothing else in the app may enumerate languages — the picker, the resource
// loader, the dayjs locale import, the Clerk localization bundle and the
// Accept-Language header all derive from this list.
//
// See docs/features/declarant-i18n/00-OVERVIEW.md.

const BASE_LOCALES = [
  {
    code: 'en',
    // Endonym: a language is always named in ITS OWN language. A user who has
    // accidentally landed in Italian must be able to find their way back
    // without reading Italian.
    label: 'English',
    // BCP-47 tag handed to Intl.*. Deliberately en-GB, not en-US: the users
    // are EU importers and every date in the product is day-first (9 sites
    // already format 'DD/MM/YYYY').
    intlTag: 'en-GB',
    // dayjs's locale NAME, which is not always the language code. This one is
    // load-bearing: dayjs's built-in `en` is US English, so `.format('ll')`
    // under it renders "Dec 31, 2026" — month-first, contradicting the
    // day-first decision above and silently disagreeing with Intl. Only
    // `en-gb` gives "31 Dec 2026". It must therefore be imported like any
    // other locale; `en` is NOT free.
    dayjsLocale: 'en-gb',
    dayjs: () => import('dayjs/locale/en-gb'),
    clerk: () => Promise.resolve(undefined), // Clerk defaults to enUS
  },
  {
    code: 'es',
    label: 'Español',
    intlTag: 'es-ES',
    dayjsLocale: 'es',
    dayjs: () => import('dayjs/locale/es'),
    clerk: () => import('@clerk/localizations').then((m) => m.esES),
  },
  {
    code: 'it',
    label: 'Italiano',
    intlTag: 'it-IT',
    dayjsLocale: 'it',
    dayjs: () => import('dayjs/locale/it'),
    clerk: () => import('@clerk/localizations').then((m) => m.itIT),
  },
  // German — the D7 proof (phase 9). Adding a language is exactly this: one
  // row here plus a src/locales/de/ folder. de is gated OFF by default via
  // REACT_APP_I18N_LOCALES below (it ships only partial translations), but the
  // architecture carries it end to end — dates, numbers, Clerk, plurals — and
  // any untranslated key falls back to English (D4). German is Latin-1, so the
  // existing PDF fonts cover it; a non-Latin locale would need font work (O17).
  {
    code: 'de',
    label: 'Deutsch',
    intlTag: 'de-DE',
    dayjsLocale: 'de',
    dayjs: () => import('dayjs/locale/de'),
    clerk: () => import('@clerk/localizations').then((m) => m.deDE),
    // Not shipped: the D7 proof. `beta` locales are hidden from the picker
    // unless explicitly named in REACT_APP_I18N_LOCALES, so the architecture
    // carries German end-to-end without offering an all-English "Deutsch" to
    // customers. Drop this flag once German is fully translated.
    beta: false,
  },
  // Czech / French / Polish — see docs/features/declarant-i18n/add-languages-cs-de-fr-pl.md.
  // Shipped: translated, terminology reconciled to the official CBAM regulation,
  // and (cs/pl) the E2 Noto Sans PDF font registered. Drop `beta` = drop the flag.
  {
    code: 'cs',
    label: 'Čeština',
    intlTag: 'cs-CZ',
    dayjsLocale: 'cs',
    dayjs: () => import('dayjs/locale/cs'),
    clerk: () => import('@clerk/localizations').then((m) => m.csCZ),
    // Latin-Extended-A (č ř ž ě ů). Covered by the Noto Sans font registered in
    // the @react-pdf report (E2); the html2pdf/canvas PDFs were always fine.
    beta: false,
  },
  {
    code: 'fr',
    label: 'Français',
    intlTag: 'fr-FR',
    dayjsLocale: 'fr',
    dayjs: () => import('dayjs/locale/fr'),
    clerk: () => import('@clerk/localizations').then((m) => m.frFR),
    beta: false,
  },
  {
    code: 'pl',
    label: 'Polski',
    intlTag: 'pl-PL',
    dayjsLocale: 'pl',
    dayjs: () => import('dayjs/locale/pl'),
    clerk: () => import('@clerk/localizations').then((m) => m.plPL),
    // Latin-Extended-A (ą ć ę ł ń ó ś ź ż): covered by the Noto Sans E2 font.
    beta: false,
  },
  // Greek — the FIRST non-Latin locale. Greek script (U+0370–03FF). Intl,
  // dayjs (`el`) and Clerk (`elGR`) all cover it, and plurals are the simple
  // one/other pair (2 forms, like en/de). @react-pdf's Helvetica has no Greek
  // glyphs, so the report registers Noto Sans (E2 — the same font as cs/pl,
  // which also covers Greek). LTR, so no bidi work.
  {
    code: 'el',
    label: 'Ελληνικά',
    intlTag: 'el-GR',
    dayjsLocale: 'el',
    dayjs: () => import('dayjs/locale/el'),
    clerk: () => import('@clerk/localizations').then((m) => m.elGR),
    beta: false,
  },
];

// Pseudo-locale (D11). QA-only: accented, expanded and bracketed English that
// makes un-externalized strings and layout overflow visible without a
// translator.
//
// `import.meta.env.PROD` is statically replaced by Vite, so both this entry and
// the src/locales/en-XA/ resources are dead-code-eliminated from a production
// build. It must never be offered to a customer.
const PSEUDO_LOCALE = {
  code: 'en-XA',
  label: 'Pseudo (QA)',
  intlTag: 'en-GB',
  dayjsLocale: 'en-gb',
  dayjs: () => import('dayjs/locale/en-gb'),
  clerk: () => Promise.resolve(undefined),
  pseudo: true,
};

const IS_PROD = typeof import.meta !== 'undefined' && import.meta.env?.PROD;

export const DEFAULT_LOCALE = 'en';

// Rollout gate (phase 9). REACT_APP_I18N_LOCALES=en,es limits which languages
// the picker offers WITHOUT removing their catalogues from the build — so a
// gated language can be re-enabled by flipping one env var, and setting it to
// `en` is a complete, instant rollback that leaves every translated string in
// the bundle harmlessly unused. English is always included: it is the fallback
// and cannot be gated away.
const parseEnabledLocales = () => {
  const raw =
    typeof process !== 'undefined' && process.env?.REACT_APP_I18N_LOCALES
      ? process.env.REACT_APP_I18N_LOCALES
      : '';
  const requested = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (requested.length === 0) return null; // unset → all configured locales
  return new Set([DEFAULT_LOCALE, ...requested]);
};

const enabled = parseEnabledLocales();
// When the env var is set, it is authoritative (a beta locale can be opted in
// by naming it). When unset, offer every non-beta locale — so a `beta: true`
// language is present in the architecture but hidden from customers until it
// is explicitly enabled.
const gatedBase = enabled
  ? BASE_LOCALES.filter((l) => enabled.has(l.code))
  : BASE_LOCALES.filter((l) => !l.beta);

// The pseudo-locale is dev/QA-only (never in a production bundle) and is not
// subject to the customer-facing rollout gate.
export const SUPPORTED_LOCALES = IS_PROD ? gatedBase : [...gatedBase, PSEUDO_LOCALE];

export const LOCALE_CODES = SUPPORTED_LOCALES.map((l) => l.code);

export const getLocaleMeta = (code) =>
  SUPPORTED_LOCALES.find((l) => l.code === code) ??
  SUPPORTED_LOCALES.find((l) => l.code === DEFAULT_LOCALE);

// Narrow a browser tag ('es-419', 'it-CH', 'en-US') to a supported code, or
// null when we ship nothing close. Never returns a tag we cannot render.
//
// Matches on the primary subtag only: we ship one variant per language, so
// 'es-419' (Latin American Spanish) and 'es-ES' both resolve to 'es'. The
// pseudo-locale is deliberately excluded — it must only ever be chosen
// explicitly, never inferred from a browser that happens to send 'en'.
export const resolveSupported = (tag) => {
  if (!tag) return null;
  const base = String(tag).toLowerCase().split('-')[0];
  // Resolve against the GATED set, not BASE_LOCALES: a browser preferring a
  // rolled-out language should land on English, not on a language the picker
  // does not offer. The pseudo-locale is excluded (never inferred).
  const match = gatedBase.find((l) => l.code.toLowerCase() === base);
  return match ? match.code : null;
};

// Read a one-shot ?lng= override off the URL. Used at boot so QA can share a
// link that opens in the pseudo-locale without touching their saved setting.
// This does NOT make locale part of routing (D6) — no route path changes, and
// the parameter is read once and never written.
export const readLocaleFromQuery = () => {
  if (typeof window === 'undefined') return null;
  try {
    const requested = new URLSearchParams(window.location.search).get('lng');
    return requested && LOCALE_CODES.includes(requested) ? requested : null;
  } catch {
    return null;
  }
};
